import { Cloud } from '@cast/shared-types';
import {
  DatabaseInstanceProtocolV2Enum,
  DboCloudProviderEnum,
  DboInstanceStatuses,
} from '@cast/types-database-optimizer';
// TODO Remove when v1 is no longer relevant
import { CloudAssetIntegrationProvider } from '@cast/types-inventory';
import { defaultPorts as v1DefaultPorts } from '../_constants';
import { StatusChipV2Color } from '@cast/design-system';

export const dboIconColors = {
  primary: '#AD7B79',
  secondary: '#D99F9C',
};

export const instanceStatusColors: Record<
  DboInstanceStatuses,
  StatusChipV2Color
> = {
  CONNECTED: 'success',
  UNDEPLOYED: 'neutral',
  ERROR: 'error',
  READY: 'info',
  STATUS_UNSPECIFIED: 'neutral',
};

const sharedStatusLabels = {
  STATUS_UNSPECIFIED: 'Unspecified',
  UNDEPLOYED: 'Undeployed',
  ERROR: 'Error',
  CONNECTED: 'Active',
} satisfies Partial<Record<DboInstanceStatuses, string>>;

export const dboAgentStatusLabels: Record<DboInstanceStatuses, string> = {
  ...sharedStatusLabels,
  READY: 'Awaiting data',
};

export const dboProxyStatusLabels: Record<DboInstanceStatuses, string> = {
  ...sharedStatusLabels,
  READY: 'Awaiting traffic',
};

const sharedStatusTooltips = {
  UNDEPLOYED: undefined,
  ERROR:
    'Communication issue. Check network connectivity or deployment status.',
  STATUS_UNSPECIFIED: undefined,
} satisfies Partial<Record<DboInstanceStatuses, string | undefined>>;

export const dboAgentStatusTooltips: Record<
  DboInstanceStatuses,
  string | undefined
> = {
  ...sharedStatusTooltips,
  READY: 'Agent is deployed and ready.',
  CONNECTED: 'Agent is connected and running.',
};

export const dboProxyStatusTooltips: Record<
  DboInstanceStatuses,
  string | undefined
> = {
  ...sharedStatusTooltips,
  READY: 'Proxy is deployed and ready to process database traffic.',
  CONNECTED: 'Proxy is actively processing database traffic.',
};

export const dboProtocolToLabel: Record<
  DatabaseInstanceProtocolV2Enum,
  string
> = {
  POSTGRESQL: 'PostgreSQL',
  MYSQL: 'MySQL',
  ORACLE: 'Oracle',
  PROTOCOL_UNSPECIFIED: 'Unknown',
};

export const dboOverviewTooltips = {
  analyzedTablesCount:
    'Total number of queries and indexes analyzed to identify optimization opportunities.',
  affectedQueriesCount:
    'Number of queries found to have performance issues that can be resolved with the advisories below.',
  affectedQueriesDbTimePercentage:
    'Percentage of total database execution time consumed by the affected queries, the potential impact of resolving all advisories.',
  totalConnections:
    'Total open connections between your application and the database, including both active and idle.',
  active: 'Connections currently executing a query or in an open transaction.',
  idle: 'Open connections not currently executing a query. These still consume memory and CPU.',
  advisories: 'Total open query and index advisories.',
  hitRate:
    'Percentage of database queries successfully served from the cache rather than being sent to the database.',
  projectedHitRate:
    'Projected percentage of database queries that would be served from the cache instead of being sent to the database.',
  dbTimeSaved:
    'Percentage of total database execution time saved through caching. Shows the performance improvement achieved by serving queries from cache instead of executing them on the database.',
  projectedDbTimeSavePercentage:
    'Projected percentage of total database execution time that would be eliminated through caching based on observed query patterns.',
  reduction:
    'Reduction of application connections reaching your database from DBO connection pooling.',
  appToProxy:
    'Connections from your application to Proxy DB. With pooling, DBO accepts these and turns them into fewer database connections.',
  proxyToDb:
    'Connections from Proxy DB to your database. With pooling, this should be lower than connections from your application to Proxy DB.',
} as const;

export const redeploymentNeededAlert = {
  title: 'Endpoint configuration changed',
  body: 'Endpoint configuration has been changed but is not yet applied to the running instance. Until redeployed, the instance will continue using the previous endpoint configuration.',
  action: 'Redeploy instance',
} as const;

export const dboSharedTooltips = {
  cantCreate:
    'You only have read-only access. Contact your organization administrator.',
  cantDelete:
    "You don't have permission to remove components. Contact your organization administrator.",
  cantUpdate:
    "You don't have permission to update components. Contact your organization administrator.",
};

export const dboCloudProviders = [
  CloudAssetIntegrationProvider.GCP,
  CloudAssetIntegrationProvider.AWS,
];

export const supportedDbTypes = [
  DatabaseInstanceProtocolV2Enum.POSTGRESQL,
  DatabaseInstanceProtocolV2Enum.MYSQL,
];

export const dboCloudproviderMap = {
  GCP: Cloud.gcp,
  AWS: Cloud.aws,
  CLOUD_PROVIDER_UNSPECIFIED: Cloud.unknown,
} as const satisfies Record<DboCloudProviderEnum, Cloud>;

export const dboInstanceProviderTooltips: Record<
  DboCloudProviderEnum | 'MANUAL',
  string
> = {
  [DboCloudProviderEnum.AWS]: 'Amazon Web Services (AWS) ',
  [DboCloudProviderEnum.GCP]: 'Google Cloud Platform (GCP)',
  [DboCloudProviderEnum.CLOUD_PROVIDER_UNSPECIFIED]: 'Unknown provider',
  MANUAL: 'Manual instance',
};

export const defaultPorts = {
  POSTGRESQL: v1DefaultPorts.PostgreSQL,
  MYSQL: v1DefaultPorts.MySQL,
  ORACLE: v1DefaultPorts.Oracle,
  PROTOCOL_UNSPECIFIED: '',
} as const satisfies Record<DatabaseInstanceProtocolV2Enum, number | string>;
